// MetricCaveatPanel -- Lane 736, the INTAKE half of Ryan's D54.1 decision
// ("caveat the number").
//
// WHY A PICKER AND NOT THE CHAT. Lane 730 measured the alternative: chat capture
// evaluated 136 real buyer messages on PROD and captured ZERO of them, and the
// deck's own free-text team-input box has never been used once. Buyers ASK, they
// do not DECLARE. But the deeper reason is not the hit rate -- it is that a
// caveat must ATTACH to a specific figure, and "those four late deliveries" only
// resolves to `delivery_ontime_pct` if a model guesses. Haiku would not even
// classify Ryan's own sentence as intelligence. A picker knows the metric with
// certainty: the binding is a CLICK, not an inference, so there is no classifier
// here to reject anything.
//
// THE CURRENT VALUE IS SHOWN WHILE THEY TYPE, and that is load-bearing twice
// over: the buyer caveats a figure they can actually see, and the server records
// that same figure as `value_at_capture` so the slide can later say "written when
// this page read 75%" once the rolling window turns the number.
//
// THE AUDIENCE NOTICE IS NOT A NICETY. delivery_reliability is supplier-facing,
// so the buyer's words are read BY THE SUPPLIER -- they hand over the rebuttal in
// writing before it is asked for. Ryan chose that knowingly; the person writing
// the sentence has to know it too, which is why the notice sits ABOVE the box
// rather than under it. Server-supplied (never hardcoded here) so an
// internal-only metric added later cannot inherit the wrong warning.
//
// FLAG-DARK: the endpoint 404s when DECK_METRIC_CAVEAT_ENABLED is off, and this
// panel then renders NOTHING -- so with the flag off the pane is byte-identical
// to before this lane.
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../config';

function MetricCaveatPanel({
  supplierId,
  slideKey,
  authHeaders,
  apiBase = API_BASE,
}) {
  const [metrics, setMetrics] = useState(null);   // null = unknown/unavailable
  const [activeKey, setActiveKey] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    if (!supplierId || !slideKey) { setMetrics(null); return; }
    try {
      const r = await fetch(
        `${apiBase}/api/decks/metric-caveats?supplier_id=${encodeURIComponent(supplierId)}`
        + `&slide=${encodeURIComponent(slideKey)}`,
        { headers: authHeaders() },
      );
      // 404 is the module-dark shape (flag off OR unknown supplier) -- render
      // nothing rather than an error, so a dark feature is invisible and not
      // broken-looking.
      if (!r.ok) { setMetrics(null); return; }
      const data = await r.json();
      const list = (data && data.metrics) || [];
      setMetrics(list);
      if (list.length) {
        setActiveKey((prev) => (prev && list.some((m) => m.metric_key === prev)
          ? prev : list[0].metric_key));
      }
    } catch (e) {
      setMetrics(null);
    }
  }, [apiBase, authHeaders, slideKey, supplierId]);

  useEffect(() => { load(); }, [load]);

  const active = (metrics || []).find((m) => m.metric_key === activeKey) || null;

  // Seed the box from what is already on file, so "edit my caveat" is the same
  // control as "write one" -- a second save SUPERSEDES the first server-side
  // (allow_multiple=False + the metric id), it never stacks a contradiction.
  useEffect(() => {
    setText(active && active.existing ? (active.existing.content || '') : '');
    setSavedAt(null);
    setError(null);
  }, [active]);

  if (!metrics || metrics.length === 0 || !active) return null;

  const maxChars = active.max_chars || 240;
  const trimmed = text.trim();
  const dirty = trimmed !== ((active.existing && active.existing.content) || '');

  const save = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${apiBase}/api/decks/metric-caveats`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: supplierId,
          metric_key: active.metric_key,
          caveat_text: trimmed,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not save the note.');
      }
      setSavedAt(Date.now());
      await load();
    } catch (e) {
      setError(e.message || 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dk-details" style={{ marginTop: '14px' }}>
      <label className="dk-efl" htmlFor="dk-caveat-metric">
        Add a note about one of these numbers
      </label>

      {metrics.length > 1 ? (
        <select
          id="dk-caveat-metric"
          className="dk-inp"
          value={activeKey}
          onChange={(e) => setActiveKey(e.target.value)}
        >
          {metrics.map((m) => (
            <option key={m.metric_key} value={m.metric_key}>
              {m.label}{m.current_value ? `: ${m.current_value}` : ''}
            </option>
          ))}
        </select>
      ) : (
        // One metric: naming it as a heading beats a select with a single option.
        <div className="dk-locknote" style={{ marginTop: 0 }}>
          <strong>{active.label}</strong>
          {active.current_value
            ? `: this page currently reads ${active.current_value}.`
            : ': no measured value on this page yet.'}
          {active.help ? ` ${active.help}` : ''}
        </div>
      )}

      {/* ABOVE the box, deliberately: it changes what a buyer chooses to write. */}
      <div className="dk-savehint" style={{ marginTop: '10px', fontWeight: 600 }}>
        {active.audience_notice}
      </div>

      <textarea
        id="dk-caveat-text"
        className="dk-ta"
        style={{ marginTop: '8px' }}
        rows={3}
        maxLength={maxChars}
        value={text}
        placeholder="e.g. Three of those four late deliveries were caused by our own late drawing revisions, not by them."
        onChange={(e) => setText(e.target.value)}
        aria-label={`Note about ${active.label}`}
      />
      <div className="dk-savehint">
        {text.length}/{maxChars} characters. Your words appear on the page exactly
        as typed, next to the number, with your name and the month.
      </div>

      {error ? (
        <div className="dk-notice-fallback" style={{ marginTop: '8px' }}>{error}</div>
      ) : null}
      {savedAt && !dirty ? (
        <div className="dk-savehint">
          Saved. Click Refresh preview to see it on the page.
        </div>
      ) : null}

      <button
        type="button"
        className="dk-btn block"
        style={{ marginTop: '8px' }}
        disabled={!trimmed || saving || !dirty}
        onClick={save}
      >
        {saving ? 'Saving…' : (active.existing ? 'Update this note' : 'Save this note')}
      </button>

      {active.existing && active.existing.value_at_capture ? (
        <div className="dk-savehint">
          The note on file was written when this page read
          {' '}{active.existing.value_at_capture}.
        </div>
      ) : null}
    </div>
  );
}

export default MetricCaveatPanel;
